import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdShield } from 'react-icons/md';

const LAST_UPDATED = '26 April 2026';
const COMPANY      = 'HCEN · Healthcare Emergency Network';
const EMAIL        = 'privacy@hcen.in';
const ADDRESS      = 'HCEN Healthcare Pvt. Ltd., India';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: 'var(--auth-page-bg)', padding: '32px 20px', fontFamily: 'Outfit, sans-serif' }}>

      {/* Back button */}
      <div style={{ maxWidth: 820, margin: '0 auto 28px' }}>
        <button onClick={() => navigate(-1)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-muted)', border: '1px solid var(--surface-strong)', borderRadius: 10, padding: '9px 18px', color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}>
          <MdArrowBack /> Back
        </button>
      </div>

      <div style={{ maxWidth: 820, margin: '0 auto', background: 'var(--surface-subtle)', border: '1px solid var(--border-color-hover)', borderRadius: 20, padding: '40px 48px', color: 'var(--text-primary)', lineHeight: 1.8 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: 'linear-gradient(135deg,#00d4aa,#00a882)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MdShield style={{ color: '#fff', fontSize: 28 }} />
          </div>
          <div>
            <h1 style={{ fontSize: 30, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg,var(--text-primary),var(--color-primary))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Privacy Policy</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>Last Updated: {LAST_UPDATED}</p>
          </div>
        </div>

        <div style={{ height: 1, background: 'var(--border-color)', margin: '24px 0' }} />

        <p style={{ color: 'var(--text-secondary)', marginBottom: 28 }}>
          {COMPANY} ("HCEN", "we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our patient mobile application, hospital dashboard, and related services.
        </p>

        {[
          {
            title: '1. Information We Collect',
            content: [
              '**Personal Identification:** Name, mobile number, date of birth, gender, email address.',
              '**Health Information:** Symptoms, medical history, uploaded reports, doctor consultations, appointment records.',
              '**Location Data:** Your approximate location to show nearby hospitals (only when you permit).',
              '**Device Information:** Device type, OS version, app version, IP address for security purposes.',
              '**Usage Data:** Pages visited, features used, interaction patterns · to improve app experience.',
            ]
          },
          {
            title: '2. How We Use Your Information',
            content: [
              'To book, manage, and confirm hospital appointments.',
              'To send OTP verification messages via WhatsApp/SMS.',
              'To provide AI-powered health guidance (symptom checker, report analyser).',
              'To show personalised nearby hospitals based on your location.',
              'To send appointment reminders and health notifications.',
              'To process payments securely for consultations.',
              'To improve our platform and fix issues.',
            ]
          },
          {
            title: '3. Healthcare Data & Confidentiality',
            content: [
              'Your health data is treated with the highest level of confidentiality.',
              'Medical reports and AI chat history are stored securely and never shared with third parties without your consent.',
              'Hospitals only see the information necessary for your appointment (name, age, symptoms, booking details).',
              'We do not sell your health data to advertisers or insurance companies.',
            ]
          },
          {
            title: '4. Data Sharing',
            content: [
              '**With Hospitals:** Only your appointment-related information is shared with the specific hospital you book with.',
              '**With Service Providers:** WhatsApp/SMS partners (MSG91) for OTP delivery; payment gateway (Razorpay) for transactions; KYC partner (Cashfree) for GST/PAN/bank verification at hospital onboarding only.',
              '**With AI Services:** Anonymised symptom data may be processed by AI services to generate health guidance.',
              '**Legal Requirements:** We may disclose data if required by law or to protect the safety of users.',
            ]
          },
          {
            title: '5. Data Security',
            content: [
              'All data is encrypted in transit using HTTPS/TLS.',
              'Data is stored on AWS (Amazon Web Services) with industry-standard security.',
              'We use OTP-based authentication · no passwords stored.',
              'Access to your data is restricted to authorised HCEN staff only.',
            ]
          },
          {
            title: '6. Data Retention',
            content: [
              'Appointment records are retained for 3 years for medical audit purposes.',
              'AI chat history is retained for 1 year to improve response quality.',
              'You may request deletion of your account and data at any time.',
            ]
          },
          {
            title: '7. Your Rights',
            content: [
              'Access: Request a copy of data we hold about you.',
              'Correction: Request correction of inaccurate data.',
              'Deletion: Request deletion of your account and personal data.',
              'Opt-Out: Unsubscribe from promotional notifications at any time.',
              'To exercise your rights, contact us at ' + EMAIL,
            ]
          },
          {
            title: '8. Children\'s Privacy',
            content: [
              'HCEN is not intended for children under 13 years of age.',
              'If a parent/guardian creates an account on behalf of a minor for medical purposes, the adult is responsible for consent.',
            ]
          },
          {
            title: '9. Changes to This Policy',
            content: [
              'We may update this Privacy Policy periodically.',
              'We will notify you of significant changes via the app or email.',
              'Continued use of HCEN after changes constitutes acceptance of the updated policy.',
            ]
          },
          {
            title: '10. Contact Us',
            content: [
              'For privacy concerns, data requests, or questions:',
              `Email: ${EMAIL}`,
              `Address: ${ADDRESS}`,
            ]
          },
        ].map(section => (
          <div key={section.title} style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#00d4aa', marginBottom: 10 }}>{section.title}</h2>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {section.content.map((item, i) => (
                <li key={i} style={{ color: 'var(--text-secondary)', fontSize: 14.5, marginBottom: 6 }}>
                  {item.startsWith('**') ? (
                    <span>
                      <strong style={{ color: 'var(--text-primary)' }}>{item.match(/\*\*(.+?)\*\*/)?.[1]}</strong>
                      {item.replace(/\*\*(.+?)\*\*/, '')}
                    </span>
                  ) : item}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div style={{ height: 1, background: 'var(--border-color)', margin: '28px 0' }} />
        <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>
          · {new Date().getFullYear()} {COMPANY} · {ADDRESS} · {EMAIL}
        </p>
      </div>
    </div>
  );
}
